import { useEffect, useState } from 'react';
import type { OrderDetail } from '@/api/types';
import { ApiError } from '@/api/client';
import { Icon } from '@/components/Icon';
import { num, qty } from '@/lib/format';
import { useFormFactor } from '@/lib/formFactor';
import { confirmOrderAction, type OrderConfirmKind } from './api';
import s from './OrderConfirmDialog.module.css';

const READONLY = import.meta.env.VITE_READONLY !== '0';
const COPY: Record<OrderConfirmKind, { title: (order: OrderDetail) => string; dismiss: string; submit: string; done: string }> = {
  cancel: { title: (order) => `取消订单 ${order.orderNo}？`, dismiss: '暂不取消', submit: '取消订单', done: '订单已取消' },
  close: { title: () => '终止剩余发货并结案？', dismiss: '继续履约', submit: '终止并结案', done: '剩余发货已终止，订单已结案' },
  refund: { title: () => '标记为申请退款？', dismiss: '暂不处理', submit: '申请退款', done: '订单已标记申请退款' },
  delete: { title: (order) => `删除订单 ${order.orderNo}？`, dismiss: '保留订单', submit: '删除订单', done: '订单已删除' },
};

export function OrderConfirmDialog({ order, kind, allowed, onClose, onSuccess }: { order: OrderDetail; kind: OrderConfirmKind; allowed: boolean; onClose: () => void; onSuccess: (kind: OrderConfirmKind) => void }) {
  const formFactor = useFormFactor();
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const copy = COPY[kind];
  const title = kind === 'close'
    ? formFactor === 'desktop' ? '确认终止剩余发货并结案？' : formFactor === 'phone' ? '终止剩余并结案？' : '终止剩余发货并结案？'
    : kind === 'refund' && formFactor === 'desktop' ? '确认将订单标记为申请退款？'
    : kind === 'delete' && formFactor === 'desktop' ? `确认删除订单 ${order.orderNo}？`
    : copy.title(order);
  const submitLabel = formFactor === 'desktop' && kind === 'cancel' ? '确认取消订单' : formFactor === 'desktop' && kind === 'refund' ? '确认申请退款' : copy.submit;
  const remaining = Math.max(0, order.lines.reduce((sum, line) => sum + num(line.quantity) - num(line.shippedQuantity), 0));
  const ordered = order.lines.reduce((sum, line) => sum + num(line.quantity), 0);
  const shipped = ordered - remaining;
  const icon = <span className={`${s.symbol} ${kind === 'close' || kind === 'delete' || kind === 'refund' && formFactor === 'desktop' ? s.redSymbol : ''}`}><Icon name={kind === 'close' ? 'flag' : kind === 'refund' ? formFactor === 'desktop' ? 'alert' : 'return' : kind === 'delete' ? 'trash' : 'alert'} size={22} /></span>;
  const close = () => { if (done) onSuccess(kind); onClose(); };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !saving) close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, onSuccess, done, saving, kind]);

  const submit = async () => {
    if (READONLY || !allowed || saving || uncertain) return;
    setSaving(true);
    setError('');
    try {
      await confirmOrderAction(order.id, kind);
      setDone(true);
    } catch (cause) {
      if (!(cause instanceof ApiError) || cause.status === 0 || cause.status >= 500) setUncertain(true);
      setError(cause instanceof Error ? cause.message : '操作失败');
    } finally { setSaving(false); }
  };

  return <div className={s.backdrop} onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) close(); }}>
    <section className={`${s.dialog} ${formFactor === 'tablet' ? s.tablet : formFactor === 'phone' ? s.phone : s.desktop} ${kind === 'close' ? s.close : ''} ${kind === 'refund' ? s.refund : ''} ${kind === 'delete' ? s.delete : ''}`} role="dialog" aria-modal="true" aria-label={title}>
      <header className={s.head}>{formFactor === 'desktop' ? <><h2>{title}</h2><button type="button" aria-label="关闭" disabled={saving} onClick={close}>×</button></> : <>{icon}<h2>{title}</h2><button type="button" aria-label="关闭" disabled={saving} onClick={close}>×</button></>}</header>
      {done ? <div className={s.done}><Icon name="check" size={24} /><b>{copy.done}</b><p>结果已由 V1 共用业务接口记录。</p><button type="button" onClick={close}>完成</button></div> : <>
        <div className={s.body}><div className={s.message}>{formFactor === 'desktop' && icon}<p>
          {kind === 'cancel' && <>取消后订单不再履约。这张订单的 <strong>{order.lines.length} 个产品共 {qty(ordered)} 件</strong>不再发货，已产生的库存预占会释放回可用库存。</>}
          {kind === 'close' && <>剩余 <strong>{qty(remaining)} 件</strong>不再发货，对应的库存预占会释放。结案后不能再新增发货。</>}
          {kind === 'refund' && <>表示客户已提出退款诉求。订单会<strong>暂停后续发货</strong>，款项和发票仍在结算里处理。</>}
          {kind === 'delete' && <>此操作不可恢复。订单和 {order.lines.length} 行明细会从列表移除，操作日志保留。</>}
        </p></div><div className={s.explain}>
          {kind === 'cancel' && <>这张订单尚未发货，可以取消。已发货的订单不能取消，只能“终止剩余并结案”。</>}
          {kind === 'close' && <><strong>会保留：</strong>已发的 {order.shipments?.length || 0} 个批次、{qty(shipped)} 件发货数量、全部出库流水。</>}
          {kind === 'refund' && <>以后可以撤销这个标记，恢复发货。</>}
          {kind === 'delete' && (order.status === 'DRAFT' ? <>只有未履约的订单能删除。这张是草稿，还没提交预占。</> : <>只有未履约的订单能删除；如有结算、开票或库存记录，V1 会拒绝删除。</>)}
        </div>
          {READONLY && <p className={s.notice}>当前为生产只读模式，可查看确认窗口，暂不能提交。</p>}
          {error && <p className={s.error} role="alert">{error}{uncertain && '。结果未确认，请先核对订单状态，暂勿重复提交。'}</p>}
        </div>
        <footer className={s.footer}><button type="button" onClick={close} disabled={saving}>{copy.dismiss}</button><button type="button" className={kind === 'refund' ? s.bluePrimary : s.primary} onClick={() => void submit()} disabled={READONLY || !allowed || saving || uncertain}>{saving ? '处理中…' : submitLabel}</button></footer>
      </>}
    </section>
  </div>;
}
